
import React from 'react';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, UserPlus } from 'lucide-react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSetupProgress } from "@/hooks/useSetupProgress";

const GettingStarted = () => {
  const {
    data: session
  } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const {
        data: {
          session
        },
        error
      } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    }
  });
  
  const { setupSteps } = useSetupProgress();
  const firstName = session?.user?.user_metadata?.firstName;
  const completedSteps = setupSteps.filter(step => step.completed).length;
  const totalSteps = setupSteps.length;

  return <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-6 space-y-2 text-center">
        <h1 className="text-3xl tracking-tight font-semibold">
          {firstName ? `Hola ${firstName}! Welcome to Smplify 👋` : 'Welcome to Smplify! 👋'}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-3xl leading-relaxed">
          Here is your all-in-one solution for device management, configuration deployment, and security enforcement. 
          Let's get your setup tailored to your needs.
        </p>
      </div>

      <div className="bg-white rounded-lg p-6 mb-10 shadow-sm border border-gray-100 flex flex-col md:flex-row items-center py-[24px] my-[40px]">
        <div className="md:w-3/5 mb-3 md:mb-0 pl-2">
          <h2 className="tracking-tight mb-2 text-xl font-medium">
            Streamline Your Device Management
          </h2>
          <p className="text-muted-foreground mb-3 max-w-sm text-sm">
            Smplify helps you manage all your devices in one place,
            deploy configurations with ease, and maintain security
            compliance with minimal effort.
          </p>
          <div className="flex flex-wrap gap-3 my-[16px]">
            <Button asChild>
              <Link to="/devices">
                <Shield className="mr-2 h-4 w-4" /> Enroll Device
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/profiles/new">
                <UserPlus className="mr-2 h-4 w-4" /> Create Profile
              </Link>
            </Button>
          </div>
        </div>
        <div className="md:w-2/5 flex justify-center">
          <img src="/lovable-uploads/40fc408c-1fe6-4629-be53-10996bedf8cd.png" alt="Device Management Illustration" className="rounded-lg max-w-full h-auto w-2/3" />
        </div>
      </div>

      <div className="mb-8 my-0 py-[16px]">
        <h2 className="text-2xl font-semibold tracking-tight mb-6 text-center">Key Setup Components</h2>
        <div className="flex flex-col md:flex-row items-start gap-4 justify-between">
          {setupSteps.map((step, index) => <div key={step.id} className="flex-1 relative">
              {index < setupSteps.length - 1 && <div className="hidden md:block absolute top-6 h-0.5 bg-gray-200" style={{
            left: '50%',
            width: '100%',
            zIndex: 0
          }}>
                  {step.completed && <div className="absolute top-0 left-0 h-full bg-green-500" style={{
              width: setupSteps[index + 1].completed ? '100%' : '50%'
            }}></div>}
                </div>}
              
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 relative z-10 ${step.completed ? 'bg-green-100 text-green-600' : index === completedSteps ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                  {step.completed ? <ArrowRight className="h-6 w-6" /> : <span className="font-bold">{index + 1}</span>}
                </div>
                <h3 className={`font-medium text-center text-sm mb-1 relative z-10 h-6 flex items-center justify-center ${step.completed ? 'text-green-700' : index === completedSteps ? 'text-blue-700' : 'text-gray-500'}`}>{step.name}</h3>
                {step.completed && <span className="text-xs text-green-600">Completed</span>}
              </div>
            </div>)}
        </div>
      </div>

      <div className="text-center mt-8">
        <h3 className="font-medium mb-2 text-base">For Additional Assistance</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Our support team is available to help you with any questions or issues.
        </p>
        <Button variant="outline" className="my-[10px]">Contact Support</Button>
      </div>
    </div>;
};

export default GettingStarted;
